/**
 * Video Capture Utility for Attendance
 * Captures 2-3 second videos for clock in/out verification
 */

export class VideoCaptureUtil {
  /**
   * Get supported MIME type for recording
   * @returns {string} - Supported MIME type
   */
  static getSupportedMimeType() {
    const types = [
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4',
      'video/quicktime'
    ]
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }
    return ''
  }

  /**
   * Record a video from camera stream
   * @param {MediaStream} stream - Camera stream
   * @param {number} duration - Duration in seconds (default: 3)
   * @returns {Promise<Blob>} - Video blob
   */
  static async recordVideo(stream, duration = 3) {
    return new Promise((resolve, reject) => {
      const mimeType = this.getSupportedMimeType()

      const options = {
        videoBitsPerSecond: 1000000,
      }

      if (mimeType) {
        options.mimeType = mimeType
      }

      let mediaRecorder
      try {
        mediaRecorder = new MediaRecorder(stream, options)
      } catch (e) {
        console.error('Failed to create MediaRecorder:', e)
        // Fallback to default if options fail
        mediaRecorder = new MediaRecorder(stream)
      }

      const chunks = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const finalType = mediaRecorder.mimeType || 'video/webm'
        const blob = new Blob(chunks, { type: finalType })
        resolve(blob)
      }

      mediaRecorder.onerror = (error) => {
        reject(error)
      }

      // Start recording
      mediaRecorder.start()

      // Stop recording after specified duration
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop()
        }
      }, duration * 1000)
    })
  }

  /**
   * Convert blob to base64 for preview or alternative storage
   * @param {Blob} blob - Video blob
   * @returns {Promise<string>} - Base64 string
   */
  static async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64 = reader.result
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  /**
   * Get video metadata
   * @param {Blob} blob - Video blob
   * @returns {Promise<Object>} - Video metadata
   */
  static async getVideoMetadata(blob) {
    return new Promise((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.muted = true
      video.playsInline = true

      const timeout = setTimeout(() => {
        cleanup()
        resolve({
          duration: 0,
          width: 0,
          height: 0,
          size: blob.size,
          type: blob.type,
          unreliable: true
        })
      }, 2000)

      const cleanup = () => {
        clearTimeout(timeout)
        if (video.src) {
          URL.revokeObjectURL(video.src)
          video.src = ''
        }
      }

      video.onloadedmetadata = () => {
        const duration = video.duration
        const width = video.videoWidth
        const height = video.videoHeight
        cleanup()
        resolve({
          duration,
          width,
          height,
          size: blob.size,
          type: blob.type,
        })
      }

      video.onerror = () => {
        cleanup()
        resolve({
          duration: 0,
          width: 0,
          height: 0,
          size: blob.size,
          type: blob.type,
          error: true
        })
      }

      video.src = URL.createObjectURL(blob)
    })
  }

  /**
   * Validate video quality
   * @param {Blob} blob - Video blob
   * @returns {Promise<Object>} - Validation result
   */
  static async validateVideo(blob) {
    try {
      const metadata = await this.getVideoMetadata(blob)

      // Check if video is too small or too large
      const minSize = 5000 // Reduced to 5KB minimum
      const maxSize = 20 * 1024 * 1024 // 20MB maximum

      if (blob.size < minSize) {
        return {
          isValid: false,
          error: 'Video file is too small. Please try again.',
        }
      }

      if (blob.size > maxSize) {
        return {
          isValid: false,
          error: 'Video file is too large. Please try again.',
        }
      }

      // Check duration (should be 2-4 seconds)
      // Browsers often report duration as Infinity or NaN for recorded blobs
      const hasDuration = metadata.duration && !isNaN(metadata.duration) && isFinite(metadata.duration)

      if (hasDuration) {
        // If we have a reliable duration, check it
        if (metadata.duration < 1.5 || metadata.duration > 5) {
          return {
            isValid: false,
            error: 'Video duration should be 2-3 seconds.',
          }
        }
      } else {
        // If duration is unreliable, we trust the blob size and the fact it just recorded
        console.warn('Video duration metadata is unreliable, skipping duration check')
      }

      return {
        isValid: true,
        metadata,
      }
    } catch (error) {
      return {
        isValid: false,
        error: 'Failed to validate video: ' + error.message,
      }
    }
  }
}

export default VideoCaptureUtil

